// Job create/edit modal with embedded spool table.
// CREATE: one POST with spools inlined — atomic insert via the
// server's transaction. UPDATE: PATCH /jobs/:id then PATCH /jobs/:id/spools;
// if the second call fails the job-level edit is already persisted.
import { useEffect, useState } from "react";
import type {
  JobDto,
  SolutionTypeDto,
  SpoolStageDto,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import {
  createJob,
  deleteJob,
  replaceJobStages,
  replaceSpoolsBulk,
  updateJob,
} from "../hooks/useProductionApi";
import { type JobFormState } from "./JobFormFields";
import {
  spoolFromDto,
  spoolToInput,
  type SpoolDraft,
} from "../components/SpoolTable";
import {
  emptyForm,
  fromJob,
  toCreatePayload,
  toUpdatePayload,
} from "./jobFormState";
import { SpoolImportModal } from "./SpoolImportModal";
import { JobModalBody } from "./JobModalBody";
import { useActiveStages } from "../components/StagesProvider";
import { deriveJobStages } from "../lib/stageHelpers";

interface Props {
  open: boolean;
  job: JobDto | null;
  initial?: JobFormState | null;
  team: TeamMemberDto[];
  types: SolutionTypeDto[];
  // When set, the spool table scrolls this row into view and flashes
  // it briefly. Driven by the ?spool= deep-link query.
  focusSpoolId?: number | null;
  onClose(): void;
  onSaved(): void;
  onManageTypes(): void;
}

export function JobModal(props: Props) {
  const { open, job, initial, team, types, focusSpoolId, onClose, onSaved, onManageTypes } = props;
  const [form, setForm] = useState<JobFormState>(emptyForm);
  const [spools, setSpools] = useState<SpoolDraft[]>([]);
  const [jobStages, setJobStages] = useState<SpoolStageDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const catalogue = useActiveStages();

  useEffect(() => {
    if (!open) return;
    if (job) setForm(fromJob(job));
    else setForm(initial ?? emptyForm());
    setSpools(job ? job.spools.map(spoolFromDto) : []);
    setJobStages(job?.jobStages ?? []);
    setError(null);
  }, [open, job, initial]);

  if (!open) return null;

  // Detailed ↔ Simple. On first flip to Simple with no pipeline yet,
  // seed it from the current draft spools (or a fresh pipeline on
  // the job's bay). Walks the live `spools` draft — not the last
  // persisted `job?.spools` — so a user who paste-imported / added /
  // removed spools before flipping sees the rollup reflect their
  // intent. Persisted stage rows are only available for drafts that
  // still match a saved spool by id; brand-new drafts contribute no
  // stage data and seed a fresh pipeline on the chosen bay.
  const setMode = (simple: boolean) => {
    setForm((s) => ({ ...s, simpleMode: simple }));
    if (simple && jobStages.length === 0) {
      const persistedById = new Map(
        (job?.spools ?? []).map((s) => [s.id, s.stages]),
      );
      const spoolsForSeed = spools.map((d) => ({
        stages: typeof d.id === "number" ? persistedById.get(d.id) ?? [] : [],
      }));
      setJobStages(deriveJobStages(spoolsForSeed, catalogue, job?.bay ?? null));
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Job name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const inputs = spools.map(spoolToInput);
      // Persist the job-stage pipeline whenever it carries rows — a
      // job seeded then flipped back to Detailed keeps its draft for
      // next time. Maps the SpoolStageDto-shaped draft to the seed
      // wire shape.
      const stageSeed = jobStages.map((st) => ({
        stageKey: st.stageKey,
        status: st.status,
        hours: st.hours,
        assigneeId: st.assigneeId,
        startDate: st.startDate,
        doneDate: st.doneDate,
      }));
      const persistStages = form.simpleMode || stageSeed.length > 0;
      if (job) {
        await updateJob(job.id, toUpdatePayload(form));
        await replaceSpoolsBulk(job.id, inputs);
        if (persistStages) await replaceJobStages(job.id, stageSeed);
      } else {
        // Inline spools in the create payload — server inserts both
        // inside a single transaction so a failed spool batch rolls
        // back the parent job, avoiding orphans.
        const created = await createJob({ ...toCreatePayload(form), spools: inputs });
        if (persistStages) await replaceJobStages(created.id, stageSeed);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!job) return;
    if (!window.confirm(`Delete job ${job.jobNumber}?`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteJob(job.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="modal modal-xl">
          <div className="modal-header">
            <h2>
              {job
                ? [
                    `Edit Job ${job.jobNumber}`,
                    job.customer,
                    job.name,
                  ]
                    .filter((s): s is string => Boolean(s && s.trim()))
                    .join(" | ")
                : "New Job"}
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <JobModeToggle
                simple={form.simpleMode}
                onChange={setMode}
              />
              <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
            </div>
          </div>
          <JobModalBody
            form={form}
            onFormChange={(p) => setForm((s) => ({ ...s, ...p }))}
            spools={spools}
            onSpoolsChange={setSpools}
            jobStages={jobStages}
            onJobStagesChange={setJobStages}
            onSuggestDueDate={(date) => setForm((s) => ({ ...s, dueDate: date }))}
            // Functional update + id dedupe — bulk-promoting several
            // drawing candidates in one render cycle queues several
            // appends and a closure-over-spools setter would drop all
            // but the last. Dedupe by id covers the case where a
            // candidate's num happens to resolve to a spool that's
            // already in the list (so promoteCandidate returns the
            // existing row instead of creating a new one).
            onAppendSpool={(spool) => setSpools((prev) =>
              prev.some((s) => s.id === spool.id)
                ? prev
                : [...prev, spoolFromDto(spool)]
            )}
            team={team}
            types={types}
            onManageTypes={onManageTypes}
            onImport={() => setImportOpen(true)}
            error={error}
            job={job}
            focusSpoolId={focusSpoolId ?? null}
            onAutoScheduled={onSaved}
          />
          <div className="modal-footer">
            {job && (
              <button className="btn-del" onClick={remove} disabled={saving} style={{ marginRight: "auto" }}>
                Delete Job
              </button>
            )}
            <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn-submit" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Job"}
            </button>
          </div>
        </div>
      </div>
      <SpoolImportModal open={importOpen} onClose={() => setImportOpen(false)}
        onImported={(added) => {
          setSpools((cur) => [...cur, ...added.map((r, i) => ({
            ...r,
            localKey: `imp-${Date.now()}-${i}`,
            sortOrder: cur.length + i,
          }))]);
          setImportOpen(false);
        }} />
    </>
  );
}

// Detailed | Simple segmented control in the modal header. Detailed
// tracks every spool through its own pipeline; Simple tracks one
// pipeline for the whole job.
function JobModeToggle({ simple, onChange }: { simple: boolean; onChange: (simple: boolean) => void }) {
  const btn = (mode: "detailed" | "simple", label: string) => {
    const active = (mode === "simple") === simple;
    return (
      <button
        type="button"
        onClick={() => onChange(mode === "simple")}
        style={{
          height: 28,
          padding: "0 12px",
          border: "none",
          borderRight: mode === "detailed" ? "1px solid var(--bsp-border-strong)" : "none",
          background: active ? "var(--bsp-blue)" : "transparent",
          color: active ? "#fff" : "var(--bsp-blue)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div
      title="Detailed tracks every spool through its own stage pipeline. Simple tracks one stage pipeline for the whole job."
      style={{
        display: "inline-flex",
        height: 30,
        alignItems: "stretch",
        border: "1px solid var(--bsp-border-strong)",
        borderRadius: "var(--bsp-r-md, 6px)",
        overflow: "hidden",
      }}
    >
      {btn("detailed", "Detailed")}
      {btn("simple", "Simple")}
    </div>
  );
}
